import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const steps = [
  {
    n: "01",
    icon: "ri-user-line",
    title: "Handicap Index",
    body: "This is the number you see on your app or score tracker — the baseline of your playing ability. It is based on your 8 best scores from your last 20 rounds, adjusted for course difficulty. Everyone receives a single Handicap Index (HI), regardless of which course they play.",
    tip: "Your Handicap Index is updated whenever a new acceptable score is processed under the WHS™.",
  },
  {
    n: "02",
    icon: "ri-flag-2-line",
    title: "Course Handicap",
    body: "The WHS™ converts your HI to the Course Handicap (CH) using the tee's Course Rating and Slope Rating. More difficult tees generally result in a higher Course Handicap. This makes every course fair, whether you are at your home club or a famous links on the coast.",
    tip: "CH = (HI × Slope / 113) + (Course Rating − Par). HANDICALC® does this automatically.",
  },
  {
    n: "03",
    icon: "ri-pie-chart-2-line",
    title: "Playing Handicap",
    body: "Your Playing Handicap (PH) is the actual number of strokes you receive in your round. It adjusts your Course Handicap by the WHS™ allowance percentage for your chosen format. Singles stroke play uses 95% of your CH. Fourball match play uses 90%. Foursomes uses 50% of the combined CH.",
    tip: "PH = CH × Allowance %. HANDICALC® applies the allowance automatically for every format.",
  },
];

const formats = [
  { format: "Singles stroke play", allow: "95%", example: "CH 14 → PH 13" },
  { format: "Singles match play", allow: "100%", example: "CH 14 → PH 14" },
  { format: "Fourball stroke play", allow: "85%", example: "CH 14 → PH 12" },
  { format: "Fourball match play", allow: "90%", example: "CH 14 → PH 13" },
  { format: "Foursomes (alternate shot)", allow: "50% combined", example: "(CH 14 + CH 12) × 50% = PH 13" },
  { format: "Greensomes", allow: "60% low / 40% high", example: "(CH 14 × 60%) + (CH 12 × 40%) = 13.2 → PH 13" },
  { format: "Scramble (4-person)", allow: "25% / 20% / 15% / 10%", example: "Weighted total of individual CHs" },
  { format: "Stableford", allow: "95%", example: "CH 14 → PH 13" },
];

const workedExamples = [
  {
    title: "Singles stroke play at a tough course",
    hi: "12.4",
    course: "Sunningdale Old (White tees)",
    slope: "139",
    cr: "74.2",
    par: "72",
    ch: "17",
    format: "Singles stroke play",
    allow: "95%",
    ph: "16",
    steps: [
      "Course Handicap",
      "(12.4 × 139 ÷ 113) + (74.2 − 72)",
      "= 17.45",
      "→ 17",
      "",
      "Playing Handicap",
      "17 × 95%",
      "= 16.15",
      "→ 16",
    ],
  },
  {
    title: "Fourball match play at an away course",
    hi: "8.2",
    course: "St Andrews Old (White tees)",
    slope: "132",
    cr: "73.1",
    par: "72",
    ch: "11",
    format: "Fourball match play",
    allow: "90%",
    ph: "10",
    steps: [
      "Course Handicap",
      "(8.2 × 132 ÷ 113) + (73.1 − 72)",
      "= 10.68",
      "→ 11",
      "",
      "Playing Handicap",
      "11 × 90%",
      "= 9.9",
      "→ 10",
    ],
  },
  {
    title: "Foursomes at a parkland course",
    hi: "16.5 / 18.2",
    course: "Wentworth East (Yellow tees)",
    slope: "128",
    cr: "72.5",
    par: "72",
    ch: "19 / 21",
    format: "Foursomes",
    allow: "50% combined",
    ph: "20",
    steps: [
      "Course Handicap",
      "Player A: (16.5 × 128 ÷ 113) + (72.5 − 72) = 19.19 → 19",
      "Player B: (18.2 × 128 ÷ 113) + (72.5 − 72) = 21.11 → 21",
      "",
      "Playing Handicap",
      "(19 + 21) × 50%",
      "= 20",
    ],
  },
];

const scenarios = [
  {
    title: "Organising a mixed-format competition",
    body: "You are running a club competition with fourball, foursomes, and singles matches across 120 members. Each format needs a different allowance percentage — HANDICALC® handles all of them so every player steps onto the tee with the correct strokes.",
  },
  {
    title: "Visiting a course for the first time",
    body: "You have never played this course before and have no idea what your Course Handicap should be. Open HANDICALC®, search the course, pick your tee — and you have your Playing Handicap before you finish your coffee.",
  },
  {
    title: "Playing a friend from different tees",
    body: "You are playing from the whites and your friend is playing from the yellows. Your raw Course Handicaps are different, but HANDICALC® calculates the correct Playing Handicap for each of you so the match is fair regardless of tee selection.",
  },
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-primary-900">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950 via-primary-900 to-primary-800"></div>
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20golf%20course%20aerial%20view%20with%20lush%20green%20fairways%20and%20bunkers%20in%20warm%20golden%20light%2C%20soft%20focus%20atmospheric%20painterly%20style%2C%20emerald%20green%20and%20warm%20sandy%20gold%20colour%20palette%2C%20editorial%20artistic%20abstract%20golf%20landscape%20suitable%20as%20dark%20background%20with%20text%20contrast&width=1600&height=900&seq=howitworks-hero-bg&orientation=landscape')] bg-cover bg-center opacity-20"></div>
          <div className="relative mx-auto max-w-content px-4 md:px-6 pt-20 md:pt-28 pb-16 md:pb-24">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur border border-background-50/20 text-xs font-medium text-accent-300">
              <i className="ri-lightbulb-line"></i>
              Understanding the WHS™
            </span>
            <h1 className="mt-6 font-heading font-bold text-background-50 text-3xl md:text-5xl lg:text-6xl leading-tight max-w-3xl">
              How WHS™ shot allowances work
            </h1>
            <p className="mt-5 text-primary-100 text-base md:text-lg max-w-2xl leading-relaxed">
              Under the World Handicap System™, your strokes are calculated in three stages. Handicap Index
              → Course Handicap → Playing Handicap. Here is how each stage works, with real-world examples and step-by-step explanations for golfers, club organisers and society secretaries.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/calculator"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                Try the calculator <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/formats"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur border border-background-50/30 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                View format allowances
              </Link>
            </div>
          </div>
        </section>

        {/* The Three Steps */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">The process</span>
              <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl leading-tight">
                How your Playing Handicap is calculated
              </h2>
              <p className="mt-4 text-foreground-700 text-base leading-relaxed">
                Every time you play, the WHS™ takes your Handicap Index and converts it twice — once for the course, once for the format.
              </p>
            </div>

            <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {steps.map((s) => (
                <article
                  key={s.n}
                  className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-8"
                >
                  <div className="flex items-center gap-4">
                    <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-600 text-background-50">
                      <i className={`${s.icon} text-xl`} aria-hidden="true"></i>
                    </span>
                    <div>
                      <span className="text-xs font-bold text-foreground-300">{s.n}</span>
                      <h3 className="font-heading font-bold text-foreground-950 text-lg md:text-xl">{s.title}</h3>
                    </div>
                  </div>
                  <p className="mt-5 text-foreground-700 text-sm leading-relaxed">{s.body}</p>
                  <div className="mt-5 rounded-lg bg-accent-100/70 border border-accent-200/50 px-4 py-3">
                    <p className="text-sm text-accent-900">
                      <i className="ri-lightbulb-line mr-1"></i>
                      <strong>Tip:</strong> {s.tip}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* The Formula */}
        <section className="bg-background-100 py-16 md:py-24 border-y border-background-200">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">The formula</span>
                <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl leading-tight">
                  Course Handicap × Allowance % = Playing Handicap
                </h2>
                <p className="mt-5 text-foreground-700 text-base leading-relaxed">
                  Every Playing Handicap is calculated in two simple steps. First, the WHS™ converts your Handicap Index
                  to a Course Handicap using the course and tee difficulty. Then, it applies the format percentage.
                </p>
                <p className="mt-4 text-foreground-700 text-base leading-relaxed">
                  The result is rounded to the nearest whole number. In match play, you may also receive strokes
                  on specific holes based on the Stroke Index, which HANDICALC® can display alongside the playing handicap.
                </p>
              </div>
              <div className="space-y-4">
                <div className="rounded-xl bg-foreground-950 text-background-50 p-6 md:p-8">
                  <div className="text-xs uppercase tracking-[0.18em] text-foreground-400 font-semibold">Step 1</div>
                  <div className="mt-3 font-heading text-xl md:text-2xl">
                    (HI × Slope / 113) + (CR − Par)
                  </div>
                  <div className="mt-1 text-sm text-foreground-400">= Course Handicap (rounded)</div>
                </div>
                <div className="rounded-xl bg-primary-900 text-background-50 p-6 md:p-8">
                  <div className="text-xs uppercase tracking-[0.18em] text-primary-200 font-semibold">Step 2</div>
                  <div className="mt-3 font-heading text-xl md:text-2xl">
                    CH × Format Allowance %
                  </div>
                  <div className="mt-1 text-sm text-primary-200">= Playing Handicap (rounded)</div>
                </div>
                <div className="rounded-xl bg-accent-500 text-foreground-950 p-6 md:p-8">
                  <div className="text-xs uppercase tracking-[0.18em] text-foreground-800 font-semibold">Example</div>
                  <div className="mt-3 font-heading text-xl md:text-2xl">
                    17 × 95% = 16.15 → <strong>16</strong>
                  </div>
                  <div className="mt-1 text-sm text-foreground-800">
                    HI 12.4 → CH 17 (White tees) → PH 16 (Singles stroke play)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Format Allowance Table */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">WHS™ format allowances</span>
              <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl leading-tight">
                Every format, every percentage
              </h2>
              <p className="mt-4 text-foreground-700 text-base leading-relaxed">
                These are the official WHS™ allowance percentages used worldwide. HANDICALC® applies them automatically for every format you select.
              </p>
            </div>

            <div className="mt-10 md:mt-14 overflow-hidden rounded-xl border border-background-200">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-background-100">
                    <tr>
                      <th className="text-left px-4 md:px-6 py-3 font-semibold text-foreground-700">Format</th>
                      <th className="text-left px-4 md:px-6 py-3 font-semibold text-foreground-700">WHS™ Allowance</th>
                      <th className="text-left px-4 md:px-6 py-3 font-semibold text-foreground-700">Example</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    {formats.map((f, i) => (
                      <tr
                        key={f.format}
                        className={i % 2 === 0 ? "bg-background-50" : "bg-background-100/50"}
                      >
                        <td className="px-4 md:px-6 py-3.5 font-medium text-foreground-950">{f.format}</td>
                        <td className="px-4 md:px-6 py-3.5">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-semibold whitespace-nowrap">
                            {f.allow}
                          </span>
                        </td>
                        <td className="px-4 md:px-6 py-3.5 text-foreground-600">{f.example}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="mt-4 text-xs text-foreground-500 text-center">
              Always verify allowances against your club's or competition's specific rules. HANDICALC® follows published WHS™ principles.
            </p>
          </div>
        </section>

        {/* Worked Examples */}
        <section className="bg-background-100 py-16 md:py-24 border-y border-background-200">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">Worked examples</span>
              <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl leading-tight">
                See the numbers in action
              </h2>
              <p className="mt-4 text-foreground-700 text-base leading-relaxed">
                Three real-world scenarios showing how the WHS™ converts a Handicap Index into a Playing Handicap.
              </p>
            </div>

            <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {workedExamples.map((ex) => (
                <article
                  key={ex.title}
                  className="rounded-xl bg-background-50 border border-background-200 p-6 md:p-8"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-600 text-background-50 text-sm font-bold">
                      {ex.ph}
                    </span>
                    <h3 className="font-heading font-semibold text-foreground-950 text-base leading-snug">{ex.title}</h3>
                  </div>
                  <div className="mt-5 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-foreground-600">Handicap Index</span>
                      <span className="font-medium text-foreground-950">{ex.hi}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-600">Course</span>
                      <span className="font-medium text-foreground-950 text-right">{ex.course}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-600">Slope / CR / Par</span>
                      <span className="font-medium text-foreground-950">{ex.slope} / {ex.cr} / {ex.par}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-600">Course Handicap</span>
                      <span className="font-medium text-foreground-950">{ex.ch}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-600">Format</span>
                      <span className="font-medium text-foreground-950">{ex.format}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-foreground-600">Allowance</span>
                      <span className="font-semibold text-secondary-800 bg-secondary-100 rounded-full px-2.5 py-0.5 text-xs">{ex.allow}</span>
                    </div>
                    <div className="pt-2 border-t border-background-200 flex justify-between">
                      <span className="font-semibold text-foreground-900">Playing Handicap</span>
                      <span className="font-heading font-bold text-foreground-950 text-lg">{ex.ph}</span>
                    </div>
                  </div>
                  <div className="mt-5 rounded-lg bg-primary-50 border border-primary-200/50 px-4 py-3">
                    <div className="text-xs text-primary-800 leading-relaxed space-y-1">
                      {ex.steps.map((line, idx) => {
                        if (line.trim() === '') return <div key={idx} className="h-1.5"></div>;
                        return <div key={idx}>{line}</div>;
                      })}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Common Scenarios */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">Use cases</span>
              <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl leading-tight">
                When you need a shot allowance
              </h2>
              <p className="mt-4 text-foreground-700 text-base leading-relaxed">
                These are the most common situations where golfers and organisers need to calculate a playing handicap correctly.
              </p>
            </div>

            <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
              {scenarios.map((s) => (
                <div
                  key={s.title}
                  className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-8"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-600 text-background-50 mb-4">
                    <i className="ri-briefcase-line text-lg"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 text-base">{s.title}</h3>
                  <p className="mt-3 text-sm text-foreground-700 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ-style mini-section */}
        <section className="bg-background-100 py-16 md:py-24 border-y border-background-200">
          <div className="mx-auto max-w-3xl px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">Common questions</span>
              <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl leading-tight">
                Quick answers about WHS™ calculations
              </h2>
            </div>

            <div className="space-y-4">
              <details className="rounded-xl bg-background-50 border border-background-200 overflow-hidden group">
                <summary className="cursor-pointer px-5 md:px-6 py-4 text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                  <span>What is the difference between Handicap Index and Course Handicap?</span>
                  <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                </summary>
                <div className="px-5 md:px-6 pb-5 text-sm text-foreground-700 leading-relaxed">
                  Your Handicap Index (HI) is a universal number representing your playing ability — it is the same at every course. Your Course Handicap (CH) converts that HI to the specific course and tee you are playing, using Slope and Course Rating. A 10 HI golfer might have a CH of 12 at a hard course and 9 at an easy one.
                </div>
              </details>
              <details className="rounded-xl bg-background-50 border border-background-200 overflow-hidden group">
                <summary className="cursor-pointer px-5 md:px-6 py-4 text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                  <span>Why do some formats give me fewer strokes than others?</span>
                  <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                </summary>
                <div className="px-5 md:px-6 pb-5 text-sm text-foreground-700 leading-relaxed">
                  The WHS™ applies different percentages depending on the format to maintain fairness. In fourball, you only play your own ball some of the time, so you receive 85% (stroke play) or 90% (match play) of your CH. In foursomes, two players share one ball, so the combined CH is halved. These percentages are published by the R&A and USGA.
                </div>
              </details>
              <details className="rounded-xl bg-background-50 border border-background-200 overflow-hidden group">
                <summary className="cursor-pointer px-5 md:px-6 py-4 text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                  <span>Do I round up or down?</span>
                  <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                </summary>
                <div className="px-5 md:px-6 pb-5 text-sm text-foreground-700 leading-relaxed">
                  The WHS™ rounds to the nearest whole number. 0.5 and above rounds up; below 0.5 rounds down. So 13.3 becomes 13, and 13.6 becomes 14. HANDICALC® handles this automatically.
                </div>
              </details>
              <details className="rounded-xl bg-background-50 border border-background-200 overflow-hidden group">
                <summary className="cursor-pointer px-5 md:px-6 py-4 text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                  <span>Can I use HANDICALC® for any course in the world?</span>
                  <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                </summary>
                <div className="px-5 md:px-6 pb-5 text-sm text-foreground-700 leading-relaxed">
                  HANDICALC® works with any course that has a published Course Rating and Slope Rating. This covers most golf courses worldwide. You simply search for the course name and select the tee you are playing from.
                </div>
              </details>
              <details className="rounded-xl bg-background-50 border border-background-200 overflow-hidden group">
                <summary className="cursor-pointer px-5 md:px-6 py-4 text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                  <span>Is the WHS™ used everywhere?</span>
                  <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                </summary>
                <div className="px-5 md:px-6 pb-5 text-sm text-foreground-700 leading-relaxed">
                  The World Handicap System™ is used by over 100 countries, including the UK, Ireland, USA, Canada, Australia, South Africa, and most of Europe and Asia. It replaced the old CONGU, USGA, and other local systems in 2020. HANDICALC® follows the published WHS™ calculation principles.
                </div>
              </details>
            </div>
          </div>
        </section>

        {/* Why golfers use HANDICALC® */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs uppercase tracking-[0.18em] text-accent-700 font-semibold">Why HANDICALC®</span>
              <h2 className="mt-3 font-heading font-bold text-foreground-950 text-3xl md:text-4xl leading-tight">
                Why golfers use HANDICALC®
              </h2>
              <p className="mt-4 text-foreground-700 text-base leading-relaxed">
                HANDICALC® is not a generic handicap calculator. It is built specifically for the situations where manual calculations get complicated.
              </p>
            </div>

            <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-8 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-lg bg-accent-500 text-foreground-950">
                  <i className="ri-equalizer-line text-xl"></i>
                </div>
                <h3 className="mt-4 font-heading font-semibold text-foreground-950 text-base">Mixed tees</h3>
                <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                  Playing from different tees? HANDICALC® adjusts each player's Course Handicap individually based on their tee selection.
                </p>
              </div>
              <div className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-8 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-lg bg-accent-500 text-foreground-950">
                  <i className="ri-group-line text-xl"></i>
                </div>
                <h3 className="mt-4 font-heading font-semibold text-foreground-950 text-base">Mixed genders</h3>
                <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                  Different Course Ratings for men and women are handled automatically — no need to look up separate charts.
                </p>
              </div>
              <div className="rounded-xl bg-background-100 border border-background-200 p-6 md:p-8 text-center">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-lg bg-accent-500 text-foreground-950">
                  <i className="ri-trophy-line text-xl"></i>
                </div>
                <h3 className="mt-4 font-heading font-semibold text-foreground-950 text-base">30+ competition formats</h3>
                <p className="mt-2 text-sm text-foreground-700 leading-relaxed">
                  Singles, fourball, foursomes, greensomes, scramble, stableford, and more — with the correct WHS™ allowance for each.
                </p>
              </div>

            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              <div className="flex items-center gap-3 rounded-xl bg-background-100 border border-background-200 px-5 py-4">
                <i className="ri-checkbox-circle-fill text-accent-600 text-lg"></i>
                <span className="text-sm text-foreground-800">Official WHS™ calculations</span>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-background-100 border border-background-200 px-5 py-4">
                <i className="ri-checkbox-circle-fill text-accent-600 text-lg"></i>
                <span className="text-sm text-foreground-800">Every competition format supported</span>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-background-100 border border-background-200 px-5 py-4">
                <i className="ri-checkbox-circle-fill text-accent-600 text-lg"></i>
                <span className="text-sm text-foreground-800">Thousands of rated courses across UK, Ireland &amp; Europe</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src="https://readdy.ai/api/search-image?query=Serene%20golf%20course%20putting%20green%20at%20sunset%20with%20warm%20golden%20light%20and%20long%20shadows%2C%20single%20flagstick%20in%20distance%2C%20painterly%20editorial%20style%2C%20deep%20emerald%20and%20warm%20gold%20colour%20palette%2C%20atmospheric%20cinematic%20composition%20suitable%20as%20dark%20background%20with%20text%20contrast&width=1600&height=800&seq=howitworks-cta-bg&orientation=landscape"
                alt="Golf course at sunset"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-900/75 to-primary-800/60"></div>
              <div className="relative z-10 px-6 md:px-14 py-14 md:py-20 text-center max-w-3xl mx-auto">
                <h2 className="font-heading font-bold text-background-50 text-3xl md:text-4xl leading-tight">
                  Ready to calculate your shots?
                </h2>
                <p className="mt-4 text-background-100/90 text-base md:text-lg leading-relaxed">
                  Open HANDICALC®, search for your course, pick your tee and format — and get the correct WHS™ playing handicap in under a second.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                  <Link
                    to="/calculator"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm md:text-base font-semibold whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Open the calculator <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/faq"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur border border-background-50/30 text-background-50 text-sm md:text-base font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Read the FAQ
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}